import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { kg } from "@/lib/calc";
import { lineColor, lineEmoji, productEmoji, productLabel, shiftLabel, SHIFT_TIME } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, Camera, Check, Wand2, X, CheckCircle2, Users, Search, Repeat, Clock, ArrowDown, Upload, Loader2, Volume2, VolumeX } from "lucide-react";
import { sfx, isMuted, setMuted } from "@/lib/sounds";
import { SlotCelebration } from "@/components/SlotCelebration";
import { reward, banner, coinRain } from "@/lib/fx";

const STEPS = ["When?", "Which line?", "Photo", "Who was on it?", "What did the line make?", "Done"];
const WINS = ["🎰 Shift locked in!", "🎰 Line locked in!", "📸 Evidence banked!", "👷 Crew locked in!", "💰 Kilos all counted!"];
const QUESTIONS = ["When was this work done?", "Which line were they on?", "Photo of the evidence (required)", "Who worked, and their numbers", "Where did the kilos go?", "Have a last look, then file it"];
const HELP = [
  { e: "🗓️", t: "First — which shift?", b: "Pick the day the shift STARTS. The week opens Sunday 11pm with the night shift 🌙, so Sunday night counts as Sunday." },
  { e: "🏭", t: "One line at a time", b: "Everything on this sheet belongs to one line + shift. Tap the line they worked." },
  { e: "📸", t: "Evidence first", b: "Take a photo of the scale or count sheet BEFORE typing any minutes or kilos. You can't continue without it." },
  { e: "👷", t: "Tick the crew, type their numbers", b: "Minutes, buckets (we do the ×kg maths for you), or weighed kilos. No product codes here — that's next." },
  { e: "⚖️", t: "Give every kilo a home", b: "Every kilo needs a product — or pick 'Other' and write the reason (e.g. damaged fillets from drumsticks). Keep going until it turns green ✓." },
  { e: "✅", t: "Last look", b: "Check each person's earned kilos, then file it. You can always edit later with a reason." },
];
const TYPES = [{ v: "buckets", l: "Buckets" }, { v: "pool", l: "Minutes" }, { v: "direct", l: "Weighed kg" }, { v: "piece", l: "Pieces" }];
const SHIFT_HOURS = { night: ["2300", "0700"], day: ["0700", "1500"], afternoon: ["1500", "2300"] };
const toMin = (s) => { const d = (s || "").replace(/\D/g, ""); if (!d) return null; const p = d.padStart(4, "0").slice(-4); const h = +p.slice(0, 2), m = +p.slice(2); if (h > 23 || m > 59) return null; return h * 60 + m; };
const calcMinutes = (a, b) => { const s = toMin(a), e = toMin(b); if (s == null || e == null) return null; let d = e - s; if (d < 0) d += 1440; return d; };
const DRAFT_KEY = "r2w_entry_draft_v2";
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function NewEntry() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [products, setProducts] = useState([]);
  const [lastCrew, setLastCrew] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dismissed, setDismissed] = useState({});
  const [crewOpen, setCrewOpen] = useState(false);
  const [crewQuery, setCrewQuery] = useState("");
  const [bulkMethod, setBulkMethod] = useState("");
  const [calc, setCalc] = useState({ start: "", end: "" });
  const [allocateLater, setAllocateLater] = useState(false);
  const [muted, setMutedS] = useState(isMuted());
  const [cele, setCele] = useState(null);
  const [sups, setSups] = useState([]);
  const wasBalanced = useRef(false);
  const toggleSound = () => { setMuted(!muted); setMutedS(!muted); if (muted) setTimeout(() => sfx.coin(), 0); };
  const [entry, setEntry] = useState(() => {
    const d = localStorage.getItem(DRAFT_KEY);
    if (d) try { return JSON.parse(d); } catch { /* ignore */ }
    return { date: new Date().toISOString().slice(0, 10), shift: "", line: "", weighed: "", workers: [], allocations: [{ product_id: "", code: "", kg: "" }], photos: [] };
  });

  useEffect(() => {
    api.get("/meta").then((r) => { setMeta(r.data); if (!entry.shift) setEntry((e) => ({ ...e, shift: r.data.shifts[0]?.key || "" })); });
    api.get("/workers").then((r) => setWorkers(r.data.filter((w) => w.active).sort((a, b) => (a.number || 0) - (b.number || 0))));
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active)));
    api.get("/entries", { params: { mine: true } }).then((r) => { if (r.data?.length) setLastCrew(r.data[0]); });
    if (isAdmin) api.get("/admin/accounts").then((r) => setSups(r.data.filter((a) => a.active && a.role === "supervisor"))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify(entry)); }, [entry]);
  useEffect(() => { const h = SHIFT_HOURS[entry.shift]; if (h) setCalc({ start: h[0], end: h[1] }); }, [entry.shift]);
  const applyMinutesAll = () => {
    const m = calcMinutes(calc.start, calc.end);
    if (m == null) return toast.error("Enter valid times like 0700 and 1400");
    const pool = entry.workers.filter((w) => w.type === "pool");
    if (!pool.length) return toast.info("No minutes-based workers to fill");
    patch({ workers: entry.workers.map((w) => (w.type === "pool" ? { ...w, number: String(m) } : w)) });
    toast.success(`Set ${m} min for ${pool.length} minutes worker${pool.length > 1 ? "s" : ""}`);
  };

  const bw = useMemo(() => {
    const wd = new Date(entry.date + "T00:00:00").getDay();
    return (meta?.bucket_weights || [8, 10, 10, 10, 10, 10, 9])[wd] ?? 10;
  }, [entry.date, meta]);

  const minutesWorkers = entry.workers.filter((w) => w.type === "pool");
  const hasMinutes = minutesWorkers.length > 0;
  const totalMinutes = minutesWorkers.reduce((s, w) => s + (Number(w.number) || 0), 0);
  const pw = useMemo(() => {
    if (!meta || (entry.line || "").trim().toLowerCase() !== (meta.pw_line || "").toLowerCase()) return null;
    const wd = new Date(entry.date + "T00:00:00").getDay();
    const hol = (meta.holidays || []).includes(entry.date);
    const key = hol || wd === 0 ? "sunph" : wd === 6 ? "sat" : entry.shift === "night" ? "a" : null;
    if (!key) return null;
    const c = (meta.pw_formula || {})[key];
    if (!c) return null;
    const kgs = round2(totalMinutes / 60 * c.rate / c.div);
    return { kg: kgs, label: `${totalMinutes} min = ${round2(totalMinutes / 60)} h × ${c.rate} ÷ ${c.div} = ${kgs} kg`, name: key === "a" ? "1st shift" : key === "sat" ? "Saturday" : "Sunday / public holiday" };
  }, [meta, entry.line, entry.date, entry.shift, totalMinutes]);
  const bucketDirect = entry.workers.reduce((s, w) => w.type === "buckets" ? s + (Number(w.number) || 0) * bw : w.type === "direct" ? s + (Number(w.number) || 0) : s, 0);
  const allocated = round2(entry.allocations.reduce((s, a) => s + (Number(a.kg) || 0), 0));
  const weighedBlank = String(entry.weighed ?? "").trim() === "";
  const target = round2(hasMinutes ? (pw ? pw.kg + bucketDirect : weighedBlank ? allocated : (Number(entry.weighed) || 0)) : bucketDirect);
  const poolAuto = hasMinutes && !pw && weighedBlank && allocated > 0;
  const remaining = round2(target - allocated);
  const hasHome = (a) => !!a.product_id || (a.reason_mode && !!(a.reason || "").trim());
  const allocValid = entry.allocations.length > 0 && entry.allocations.every((a) => hasHome(a) && Number(a.kg) > 0);
  const missingHome = entry.allocations.some((a) => Number(a.kg) > 0 && !hasHome(a));
  const balanced = allocValid && target > 0 && Math.abs(remaining) < 0.01;
  const laterReason = (entry.later_reason || "").trim();
  const laterOk = allocateLater && !!laterReason;
  useEffect(() => { if (balanced && !wasBalanced.current && step === 4) { sfx.ding(); coinRain(); } wasBalanced.current = balanced; }, [balanced, step]);

  const earnedFor = (w) => {
    if (w.type === "pool") { const pk = pw ? pw.kg : target; return totalMinutes > 0 && pk > 0 ? round2((Number(w.number) || 0) / totalMinutes * pk) : 0; }
    if (w.type === "buckets") return round2((Number(w.number) || 0) * bw);
    if (w.type === "direct") return round2(Number(w.number) || 0);
    return 0;
  };

  const patch = (p) => setEntry((e) => ({ ...e, ...p }));
  const setW = (i, p) => patch({ workers: entry.workers.map((w, idx) => (idx === i ? { ...w, ...p } : w)) });
  const delW = (i) => patch({ workers: entry.workers.filter((_, idx) => idx !== i) });
  const addAlloc = () => patch({ allocations: [...entry.allocations, { product_id: "", code: "", kg: "" }] });
  const setA = (i, p) => patch({ allocations: entry.allocations.map((a, idx) => (idx === i ? { ...a, ...p } : a)) });
  const delA = (i) => patch({ allocations: entry.allocations.filter((_, idx) => idx !== i) });

  const workerById = (id) => workers.find((w) => w.id === id);
  const inCrew = (id) => entry.workers.some((w) => w.worker_id === id);
  const toggleCrew = (w) => {
    if (inCrew(w.id)) sfx.tick(); else reward();
    if (inCrew(w.id)) patch({ workers: entry.workers.filter((x) => x.worker_id !== w.id) });
    else patch({ workers: [...entry.workers, { worker_id: w.id, name: w.name, type: "buckets", number: "" }] });
  };
  const sameCrew = () => {
    if (!lastCrew?.workers?.length) return toast.info("No previous shift to copy");
    patch({ workers: lastCrew.workers.map((w) => ({ worker_id: w.worker_id, name: w.name, type: w.type || "buckets", number: "" })) });
    toast.success(`Loaded ${lastCrew.workers.length} from ${lastCrew.line} ${lastCrew.shift}`);
  };
  const applyMethodAll = (v) => {
    setBulkMethod(v);
    patch({ workers: entry.workers.map((w) => ({ ...w, type: v })) });
    toast.success(`Set everyone to ${TYPES.find((t) => t.v === v)?.l || v}`);
  };
  const onCrewKey = (ev) => {
    if (ev.key !== "Enter") return;
    ev.preventDefault();
    const q = crewQuery.trim();
    if (!/^\d+$/.test(q)) return;
    const match = workers.find((w) => Number(w.number) === Number(q));
    if (!match) return toast.error(`No worker #${q} on the roster`);
    const was = inCrew(match.id);
    toggleCrew(match);
    toast.success(`${was ? "Removed" : "Added"} #${match.number} ${match.name}`);
    setCrewQuery("");
  };

  const addNewWorker = async () => {
    const name = window.prompt("New worker name (not on the list)");
    if (!name?.trim()) return;
    try {
      const { data } = await api.post("/quick/worker", { name: name.trim(), code: "", active: true });
      setWorkers((p) => (p.some((x) => x.id === data.id) ? p : [...p, data]));
      if (!inCrew(data.id)) patch({ workers: [...entry.workers, { worker_id: data.id, name: data.name, type: "buckets", number: "" }] });
      toast.success(`Added ${data.name}`);
    } catch { toast.error("Could not add worker"); }
  };
  const addNewProduct = async (i) => {
    const code = window.prompt("New product code / placeholder");
    if (!code?.trim()) return;
    try { const { data } = await api.post("/quick/product", { code: code.trim(), name: code.trim(), family: "", active: true }); setProducts((p) => (p.some((x) => x.id === data.id) ? p : [...p, data])); setA(i, { product_id: data.id, code: data.code }); toast.success(`Added ${data.code}`); }
    catch { toast.error("Could not add product"); }
  };

  const autoFill = () => {
    if (!entry.allocations.some(hasHome)) return toast.error("Pick a product (or write a reason) first");
    if (!target) return toast.error(hasMinutes ? "Enter the weighed total first" : "Add the crew's numbers first");
    const last = entry.allocations.length - 1;
    setA(last, { kg: round2((Number(entry.allocations[last].kg) || 0) + remaining) });
    toast.success(`Filled ${kg(remaining)} kg — all kilos have a home ✓`);
  };

  const autoSplit = () => {
    if (!target) return toast.error(hasMinutes ? "Enter the weighed total first" : "Add the crew's numbers first");
    if (remaining <= 0) return toast.error("Nothing left to split — kilos are already allocated");
    const empties = entry.allocations.map((a, i) => ({ a, i })).filter(({ a }) => hasHome(a) && !(Number(a.kg) > 0));
    if (!empties.length) return toast.error("Add product rows (with no weight) to split into");
    const each = round2(remaining / empties.length);
    const allocs = [...entry.allocations];
    empties.forEach(({ i }, idx) => {
      const val = idx === empties.length - 1 ? round2(remaining - each * (empties.length - 1)) : each;
      allocs[i] = { ...allocs[i], kg: val };
    });
    patch({ allocations: allocs });
    toast.success(`Split ${kg(remaining)} kg across ${empties.length} product${empties.length > 1 ? "s" : ""} ✓`);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch({ photos: [...entry.photos, data] });
      reward();
      toast.success(`${file.type === "application/pdf" ? "Document" : "Photo"} added ✓`);
    } catch { sfx.buzz(); toast.error("Upload failed — please try again"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        date: entry.date, shift: entry.shift, line: entry.line,
        pool_kgs: hasMinutes ? (pw ? pw.kg : target) : 0,
        workers: entry.workers.map((w) => ({ worker_id: w.worker_id, name: w.name, type: w.type, number: Number(w.number) || 0 })),
        allocations: entry.allocations.filter(hasHome).map((a) => (a.product_id ? { product_id: a.product_id, code: a.code, kg: Number(a.kg) || 0 } : { product_id: "", code: "REASON", reason: a.reason.trim(), kg: Number(a.kg) || 0 })),
        photos: entry.photos.map((p) => ({ ...p, caption: (p.caption || "").trim() })),
        later_reason: allocateLater ? laterReason : "",
        filed_for: isAdmin ? entry.filed_for || "" : "",
      };
      const { data } = await api.post("/entries", payload);
      localStorage.removeItem(DRAFT_KEY);
      toast.success("Sheet filed 🎉");
      const a = meta?.week_anchor;
      const wk = a ? Math.max(1, Math.floor((new Date(entry.date + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000 / 7) + 1) : null;
      const win = data.status === "BALANCED";
      if (win) sfx.jackpot(); else sfx.nearMiss();
      setCele({ win, streak: data.streak || 0, to: wk ? `/dashboard?week=${wk}` : "/dashboard" });
    } catch (e) { sfx.buzz(); toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const workersValid = entry.workers.length > 0 && entry.workers.every((w) => Number(w.number) > 0);
  const canNext = () => {
    if (step === 0) return entry.date && entry.shift;
    if (step === 1) return entry.line;
    if (step === 2) return isAdmin || entry.photos.length > 0;
    if (step === 3) return workersValid;
    if (step === 4) return !missingHome && (balanced || laterOk);
    return true;
  };

  const filteredWorkers = workers.filter((w) => `#${w.number} ${w.name} ${w.code || ""}`.toLowerCase().includes(crewQuery.toLowerCase()));
  const help = HELP[step];
  const dayName = new Date(entry.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long" });

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] md:min-h-screen bg-[#f2fbf9] overflow-x-clip">
      <div className="sticky top-16 md:top-0 z-10 bg-white border-b border-slate-100 px-4 pt-3 pb-0">
        <div className="flex items-center justify-between text-sm mb-2">
          <button data-testid="wizard-cancel" onClick={() => { sfx.tick(); if (step > 0) setStep(step - 1); else navigate("/dashboard"); }} className="text-teal-600 font-medium">← {step > 0 ? "Back" : "Cancel"}</button>
          <span className="flex items-center gap-2">
            <span className="text-slate-400 font-medium" data-testid="step-counter">STEP <span key={step} className="slot-num font-num font-bold text-amber-500">{step + 1}</span> OF {STEPS.length}</span>
            <button data-testid="sound-toggle" onClick={toggleSound} aria-label={muted ? "Turn sound on" : "Mute sound"} className={`w-9 h-9 grid place-items-center rounded-full border transition-colors ${muted ? "border-slate-200 text-slate-400" : "border-amber-300 bg-amber-50 text-amber-600"}`}>{muted ? <VolumeX size={17} /> : <Volume2 size={17} />}</button>
          </span>
        </div>
        <div className="flex gap-1.5 mb-2" data-testid="step-bulbs">{STEPS.map((s, i) => <span key={s} className={`bulb ${i < step ? "on" : i === step ? "now" : ""}`} style={{ animationDelay: `${i * 0.15}s` }} />)}</div>
        <div className="text-xs text-slate-400 font-medium pb-2">{STEPS[step]}</div>
      </div>

      <div className="flex-1 p-4 md:p-6 space-y-4 animate-slide-in max-w-2xl w-full mx-auto box-border" key={step} style={{ paddingBottom: "calc(7.5rem + env(safe-area-inset-bottom))" }}>
        <h2 className="font-heading font-extrabold text-2xl text-slate-800 tracking-tight">{QUESTIONS[step]}</h2>
        {!dismissed[step] && (
          <div className="help-card rounded-2xl p-4 flex gap-3 bg-amber-50 border-2 border-amber-200">
            <div className="text-2xl leading-none">{help.e}</div>
            <div className="flex-1"><div className="font-heading font-bold text-amber-900 text-sm">{help.t}</div><div className="text-amber-800 text-xs mt-0.5">{help.b}</div></div>
            <button data-testid="help-dismiss" onClick={() => setDismissed({ ...dismissed, [step]: true })} className="self-start bg-amber-200/70 rounded-full text-xs font-semibold text-amber-900 px-3 py-1 whitespace-nowrap">Got it</button>
          </div>
        )}

        {step === 0 && (
          <div className="space-y-4">
            <Card>
              <Label>The day the shift STARTS</Label>
              <Input data-testid="entry-date" type="date" className="h-14 text-base rounded-xl mt-1" value={entry.date} onChange={(e) => { reward(); patch({ date: e.target.value }); }} />
              <p className="text-xs text-slate-400 mt-1.5">{dayName} · the week starts Sunday 11pm 🌙</p>
            </Card>
            {isAdmin && sups.length > 0 && (
              <Card>
                <Label>Filing for which supervisor?</Label>
                <select data-testid="filed-for" value={entry.filed_for || ""} onChange={(e) => { reward(); patch({ filed_for: e.target.value }); }} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-base mt-1">
                  <option value="">Myself (admin log)</option>
                  {sups.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1.5">Shows on their accuracy bar as admin-assisted. It doesn't count towards their %.</p>
              </Card>
            )}
            <div className="space-y-2">
              {(meta?.shifts || []).map((sh) => (
                <button key={sh.key} data-testid={`shift-${sh.key}`} onClick={() => { reward(); patch({ shift: sh.key }); }}
                  className={`w-full rounded-2xl border-2 px-4 py-4 text-left flex items-center justify-between transition-colors ${entry.shift === sh.key ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-white"}`}>
                  <span className="flex flex-col">
                    <span className="font-heading font-bold text-slate-800 text-base">{shiftLabel(sh.key)}</span>
                    <span className="text-xs text-slate-400 mt-0.5">{SHIFT_TIME[sh.key] || ""}</span>
                  </span>
                  {entry.shift === sh.key && <CheckCircle2 className="text-teal-500" strokeWidth={2.5} />}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-2 gap-2">
            {(meta?.lines || []).map((l) => (
              <button key={l} data-testid={`line-${l.replace(/[ /]/g, "-")}`} onClick={() => { reward(); patch({ line: l }); }}
                className={`rounded-2xl border-2 p-4 text-left transition-colors min-h-[88px] ${entry.line === l ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-white"}`}>
                <span className="text-3xl">{lineEmoji(l)}</span>
                <span className="font-heading font-bold text-slate-800 text-sm block mt-1.5">{l}</span>
                <span className="inline-block w-8 h-1.5 rounded-full mt-1" style={{ backgroundColor: lineColor(l) }} />
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button data-testid="open-crew" onClick={() => setCrewOpen(true)} className="flex-1 h-14 gap-2 btn-grad text-white rounded-2xl text-base">
                <Users size={20} strokeWidth={2.5} /> Tick the crew ({entry.workers.length})
              </Button>
              {lastCrew && <Button data-testid="same-crew" variant="outline" onClick={sameCrew} className="h-14 gap-1.5 rounded-2xl border-2 border-teal-300 text-teal-700"><Repeat size={18} strokeWidth={2.5} /> Same as last</Button>}
            </div>
            {entry.workers.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Tap "Tick the crew" to search by # or name.</p>}
            {entry.workers.length > 0 && (
              <div className="flex items-center gap-2 bg-white rounded-2xl border border-slate-100 p-3">
                <span className="text-sm font-semibold text-slate-600 whitespace-nowrap">Method for everyone</span>
                <Select value={bulkMethod} onValueChange={applyMethodAll}>
                  <SelectTrigger data-testid="method-all" className="h-11 flex-1 rounded-xl"><SelectValue placeholder="Set for all…" /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {entry.workers.some((w) => w.type === "pool") && (
              <div className="flex items-start gap-2 text-xs text-teal-800 bg-teal-50 rounded-xl px-3 py-2">
                <span className="text-base leading-none">⏱️</span>
                <span>Minutes workers: we split the pile by each person's share of the minutes.</span>
              </div>
            )}
            {entry.workers.some((w) => w.type === "pool") && (
              <div className="bg-white rounded-2xl border border-teal-100 p-3 space-y-2" data-testid="minutes-calc">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Clock size={16} className="text-teal-600" strokeWidth={2.5} /> Minutes calculator</div>
                <div className="flex items-center gap-2">
                  <Input data-testid="calc-start" inputMode="numeric" placeholder="0700" maxLength={4} className="h-11 w-20 font-num rounded-xl text-center" value={calc.start} onChange={(e) => setCalc({ ...calc, start: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
                  <span className="text-slate-400">→</span>
                  <Input data-testid="calc-end" inputMode="numeric" placeholder="1400" maxLength={4} className="h-11 w-20 font-num rounded-xl text-center" value={calc.end} onChange={(e) => setCalc({ ...calc, end: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
                  <span data-testid="calc-result" className="ml-1 text-sm font-num font-bold text-teal-700">{calcMinutes(calc.start, calc.end) != null ? `= ${calcMinutes(calc.start, calc.end)} min` : "—"}</span>
                </div>
                <Button data-testid="calc-apply" onClick={applyMinutesAll} className="w-full h-11 btn-grad text-white rounded-xl gap-1.5">Apply to all minutes workers</Button>
                <p className="text-[11px] text-slate-400">24-hr time — e.g. 0700 → 1400 = 420 min. Handles overnight (2300 → 0700 = 480). Each worker's box still overrides.</p>
              </div>
            )}
            {entry.workers.length > 0 && bucketDirect > 0 && !hasMinutes && (
              <div data-testid="crew-total-auto" className="btn-grad text-white rounded-2xl px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-white/85">Total pool kilos <span className="text-white/60">(added up for you)</span></span>
                <span className="font-num font-bold text-xl">{kg(bucketDirect)} kg</span>
              </div>
            )}
            {entry.workers.map((w, i) => {
              const info = workerById(w.worker_id);
              return (
                <Card key={w.worker_id || i} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {info?.number != null && <span className="font-mono text-teal-600 text-sm font-semibold">#{info.number}</span>}
                      <span className="font-heading font-bold text-slate-800">{w.name}</span>
                      {info?.code && <span className="text-slate-400 text-xs font-num">{info.code}</span>}
                    </div>
                    <button data-testid={`worker-del-${i}`} onClick={() => delW(i)} className="text-rose-400"><Trash2 size={18} strokeWidth={2.5} /></button>
                  </div>
                  <div className="flex gap-2">
                    <Select value={w.type} onValueChange={(v) => setW(i, { type: v })}>
                      <SelectTrigger data-testid={`worker-type-${i}`} className="h-12 flex-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input data-testid={`worker-num-${i}`} type="number" inputMode="decimal" placeholder={w.type === "pool" ? "minutes" : w.type === "buckets" ? "buckets" : w.type === "direct" ? "kg" : "pieces"}
                      className="h-12 w-28 font-num rounded-xl text-base" value={w.number} onChange={(e) => setW(i, { number: e.target.value })} onBlur={() => Number(w.number) > 0 && reward()} />
                  </div>
                  {w.type === "buckets" && Number(w.number) > 0 && (
                    <div className="flex items-center justify-between text-xs bg-teal-50 rounded-lg px-3 py-2 font-num">
                      <span className="text-slate-500">{w.number} buckets × {bw} kg</span>
                      <span data-testid={`worker-earned-${i}`} className="text-teal-700 font-semibold">= {kg(earnedFor(w))} kg ✓</span>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            {hasMinutes && pw && (
              <div data-testid="pw-calc" className="btn-grad text-white rounded-2xl px-4 py-3">
                <div className="flex items-center justify-between"><span className="text-sm text-white/85">Portion Wing kilos ({pw.name} formula)</span><span className="font-num font-bold text-xl">{kg(pw.kg)} kg</span></div>
                <div className="text-xs text-white/80 font-num mt-1" data-testid="pw-calc-working">{pw.label}</div>
              </div>
            )}
            {hasMinutes && !pw && (
              <Card>
                <Label>How many kilos did the line weigh in total?</Label>
                <Input data-testid="weighed-total" type="number" inputMode="decimal" placeholder={allocated > 0 ? `auto: ${kg(allocated)} kg — or type to override` : "e.g. 1000"} className="h-14 text-lg font-num rounded-xl mt-1" value={entry.weighed} onChange={(e) => patch({ weighed: e.target.value })} />
                {poolAuto ? (
                  <p data-testid="pool-auto-note" className="text-xs text-teal-600 mt-1.5 font-medium">✓ Auto-totalled from products: {kg(allocated)} kg — the {totalMinutes} minutes split this between the crew. Type a number to override.</p>
                ) : (
                  <p className="text-xs text-slate-400 mt-1.5">The {totalMinutes} minutes will split this between the crew.</p>
                )}
                {!weighedBlank && allocated > 0 && (
                  <Button data-testid="weighed-auto" size="sm" variant="outline" onClick={() => patch({ weighed: "" })} className="mt-2 gap-1 rounded-full border-teal-300 text-teal-700"><Wand2 size={14} strokeWidth={2.5} /> Use products total ({kg(allocated)} kg)</Button>
                )}
              </Card>
            )}
            {!hasMinutes && (
              <div className="btn-grad text-white rounded-2xl px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-white/85">The line made</span><span className="font-num font-semibold text-xl">{kg(target)} kg</span>
              </div>
            )}
            {entry.allocations.map((a, i) => {
              const prod = products.find((p) => p.id === a.product_id);
              return (
                <Card key={i} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{prod ? productEmoji(prod) : "🍖"}</span>
                    <Select value={a.reason_mode ? "__reason" : a.product_id} onValueChange={(v) => { reward(); if (v === "__reason") return setA(i, { product_id: "", code: "", reason_mode: true, reason: a.reason || "" }); const p = products.find((x) => x.id === v); setA(i, { product_id: v, code: p?.code || "", reason_mode: false }); }}>
                      <SelectTrigger data-testid={`alloc-product-${i}`} className="h-12 flex-1 rounded-xl"><SelectValue placeholder="Which product?" /></SelectTrigger>
                      <SelectContent><SelectItem value="__reason" data-testid={`alloc-reason-option-${i}`}>✏️ Other — write a reason</SelectItem>{products.map((p) => <SelectItem key={p.id} value={p.id}>{productEmoji(p)} {productLabel(p)}{p.name && p.name !== p.code ? <span className="text-slate-400 text-xs ml-1 font-num">{p.code}</span> : null}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {a.reason_mode && <Input data-testid={`alloc-reason-${i}`} placeholder="Reason for these kilos, e.g. damaged fillets from drumsticks" className="h-12 rounded-xl text-base" value={a.reason || ""} onChange={(e) => setA(i, { reason: e.target.value })} onBlur={() => (a.reason || "").trim() && reward()} />}
                  <div className="flex items-center gap-2">
                    <Input data-testid={`alloc-kg-${i}`} type="number" inputMode="decimal" placeholder="kg" className="h-12 flex-1 font-num rounded-xl text-base" value={a.kg} onChange={(e) => setA(i, { kg: e.target.value })} onBlur={() => Number(a.kg) > 0 && reward()} />
                    <button data-testid={`alloc-add-new-${i}`} onClick={() => addNewProduct(i)} className="px-3 h-12 rounded-xl border-2 border-teal-500 text-teal-600 text-sm font-bold whitespace-nowrap active:scale-95 transition-transform">+ New</button>
                    <button data-testid={`alloc-del-${i}`} onClick={() => delA(i)} className="w-11 h-12 grid place-items-center rounded-xl text-rose-400 border border-slate-100"><Trash2 size={18} strokeWidth={2.5} /></button>
                  </div>
                </Card>
              );
            })}
            {missingHome && (
              <div data-testid="reason-hint" className="text-red-600 text-sm font-semibold">Every row with kilos needs a product — or choose “Other” and write the reason.</div>
            )}
            {!balanced && !allocateLater && remaining > 0 && (
              <div data-testid="balance-hint" className="flex items-center gap-2 text-red-600 text-sm font-semibold">
                <ArrowDown className="animate-bounce shrink-0" size={22} strokeWidth={3} />
                <span>{kg(remaining)} kg still need a home — fill the weights or tap <b>Auto-split</b>. You can't file until it balances.</span>
              </div>
            )}
            <Button data-testid="add-alloc" variant="outline" onClick={addAlloc} className="w-full h-14 gap-1.5 rounded-2xl border-2 border-dashed border-teal-300 text-teal-700 bg-white"><Plus size={18} strokeWidth={2.5} /> Add product</Button>
            <div className="flex gap-2">
              <Button data-testid="autofill" onClick={autoFill} variant="outline" className="flex-1 h-14 gap-1.5 rounded-2xl border-2 border-teal-300 text-teal-700 bg-white"><Wand2 size={18} strokeWidth={2.5} /> Auto-fill last</Button>
              <Button data-testid="auto-split" onClick={autoSplit} className="flex-1 h-14 gap-1.5 rounded-2xl btn-grad text-white"><Wand2 size={18} strokeWidth={2.5} /> Auto-split</Button>
            </div>
            {/* Countdown to zero */}
            <div data-testid="countdown" className={`rounded-2xl border-2 p-4 ${balanced ? "border-green-300 bg-green-50" : laterOk ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
              <div className="flex items-center justify-between text-sm"><span className="font-semibold text-slate-600">The line made</span><span className="font-num font-bold text-slate-800">{kg(target)} kg</span></div>
              <div className="flex items-center justify-between text-sm mt-0.5"><span className="font-semibold text-slate-600">Given a home</span><span className="font-num font-bold text-slate-800">{kg(allocated)} kg</span></div>
              <div className="mt-2 text-sm text-center font-semibold">
                {balanced ? <span className="text-green-700">✓ All kilos have a home!</span>
                  : laterOk ? <span className="text-amber-700">You'll finish this from the dashboard later.</span>
                    : remaining > 0 ? <span className="text-slate-700">{kg(remaining)} kg still need a home</span>
                      : remaining === 0 ? <span className="text-slate-500">Enter the crew's numbers and product kilos</span>
                      : <span className="text-red-600">{kg(-remaining)} kg too many — take some off</span>}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox data-testid="allocate-later" checked={allocateLater} onCheckedChange={(v) => { sfx.tick(); setAllocateLater(!!v); }} /> Not sure yet — let me finish this later</label>
            {allocateLater && !balanced && (
              <div className="space-y-1">
                <Input data-testid="later-reason" placeholder="Why? e.g. waiting on weights from the scale room" className={`h-12 rounded-xl text-base ${laterReason ? "" : "border-red-300"}`} value={entry.later_reason || ""} onChange={(e) => patch({ later_reason: e.target.value })} />
                {!laterReason && <p data-testid="later-reason-hint" className="text-xs font-semibold text-red-600">A reason is required to finish later.</p>}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <label data-testid="photo-input-label" className={`block bg-white rounded-2xl border-2 border-dashed border-teal-300 p-6 text-center transition-transform ${uploading ? "opacity-50 pointer-events-none" : "cursor-pointer active:scale-[0.98]"}`}>
                <Camera className="mx-auto text-teal-600 mb-2" strokeWidth={2} />
                <span className="text-teal-700 font-medium text-sm">Take a photo</span>
                <input data-testid="photo-input" type="file" accept="image/*" capture="environment" className="hidden" disabled={uploading} onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }} />
              </label>
              <label data-testid="photo-upload-label" className={`block bg-white rounded-2xl border-2 border-dashed border-sky-300 p-6 text-center transition-transform ${uploading ? "opacity-50 pointer-events-none" : "cursor-pointer active:scale-[0.98]"}`}>
                <Upload className="mx-auto text-sky-600 mb-2" strokeWidth={2} />
                <span className="text-sky-700 font-medium text-sm">Upload photo / doc</span>
                <input data-testid="photo-upload-input" type="file" accept="image/*,application/pdf" className="hidden" disabled={uploading} onChange={(e) => { uploadPhoto(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            {uploading && (
              <div data-testid="upload-loading" className="flex items-center justify-center gap-2 rounded-2xl bg-teal-50 border border-teal-200 text-teal-700 text-sm font-semibold py-3">
                <Loader2 className="animate-spin" size={18} strokeWidth={2.5} /> Please wait while your photo / document is loading…
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {entry.photos.map((p, i) => (
                <div key={i} className="relative space-y-1">
                  <div className="aspect-square bg-teal-50 rounded-2xl grid place-items-center text-teal-700 text-xs p-2 text-center font-num">📸 {p.filename || "photo"}</div>
                  <Input data-testid={`photo-caption-${i}`} placeholder="Caption (optional)" maxLength={120} className="h-9 rounded-lg text-xs" value={p.caption || ""} onChange={(e) => patch({ photos: entry.photos.map((x, idx) => (idx === i ? { ...x, caption: e.target.value } : x)) })} onBlur={() => (p.caption || "").trim() && reward()} />
                  <button onClick={() => patch({ photos: entry.photos.filter((_, idx) => idx !== i) })} className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full w-5 h-5 grid place-items-center"><X size={12} /></button>
                </div>
              ))}
            </div>
            {!entry.photos.length && <p data-testid="photo-required-note" className="text-sm text-rose-600 text-center font-semibold">{isAdmin ? "Admin: a photo is optional — tap Next to skip." : "A photo is required before you can enter the crew's minutes or kilos."}</p>}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-2.5">
            <Card className="flex items-center gap-3">
              <span className="text-3xl">{lineEmoji(entry.line)}</span>
              <div><div className="font-heading font-bold text-slate-800">{entry.line}</div><div className="text-sm text-slate-400 capitalize">{entry.shift} · {dayName} {entry.date}</div></div>
            </Card>
            <div className="bg-white rounded-2xl border border-slate-100 p-4">
              <div className="text-sm font-semibold text-slate-700 mb-2">Each person's earned kilos</div>
              {entry.workers.map((w, i) => (
                <div key={i} className="flex justify-between border-b border-slate-100 py-1.5 text-sm last:border-0">
                  <span className="text-slate-700">{w.name} <span className="text-slate-400 text-xs">· {w.type === "pool" ? `${w.number} min` : w.type === "buckets" ? `${w.number} buckets` : w.type === "direct" ? `${w.number} kg` : `${w.number} pcs`}</span></span>
                  <span data-testid={`review-earned-${i}`} className="font-num font-semibold text-teal-700">{w.type === "piece" ? "—" : `${kg(earnedFor(w))} kg`}</span>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-4">
              <div className="text-sm font-semibold text-slate-700 mb-2">What the line made</div>
              {entry.allocations.filter(hasHome).map((a, i) => {
                const prod = products.find((p) => p.id === a.product_id);
                return <div key={i} className="flex justify-between border-b border-slate-100 py-1.5 text-sm last:border-0"><span className="text-slate-700">{prod ? productEmoji(prod) : "✏️"} {prod ? productLabel(prod) : `Reason: ${a.reason}`}</span><span className="font-num font-semibold text-slate-800">{kg(a.kg)} kg</span></div>;
              })}
            </div>
            {isAdmin && entry.filed_for && <p data-testid="review-filed-for" className="text-sm text-indigo-800 bg-indigo-50 rounded-xl px-3 py-2">Filing for <b>{sups.find((s) => s.id === entry.filed_for)?.name || "a supervisor"}</b>. It shows on their accuracy bar as admin-assisted.</p>}
            <div className={`rounded-2xl p-4 font-heading font-bold flex items-center justify-between ${balanced ? "bg-green-50 text-green-700" : laterOk ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"}`}>
              <span className="flex items-center gap-2"><CheckCircle2 size={20} /> Balance</span>
              <span data-testid="review-balance">{balanced ? "✓ Balanced" : laterOk ? "Finishing later" : `${kg(Math.abs(remaining))} kg off`}</span>
            </div>
            {!balanced && laterOk && <p data-testid="review-later-reason" className="text-sm text-amber-800 bg-amber-50 rounded-xl px-3 py-2">Finishing later because: {laterReason}</p>}
          </div>
        )}
      </div>

      <div className="sticky bottom-[68px] md:bottom-0 bg-white border-t border-slate-100 p-4 max-w-2xl w-full mx-auto" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
        {step < 5 ? (
          <Button data-testid="wizard-next" disabled={!canNext()} onClick={() => { sfx.reel(); banner(WINS[step]); setStep(step + 1); }} className="w-full h-14 text-base btn-grad text-white rounded-2xl">Next: {STEPS[step + 1]} →</Button>
        ) : (
          <Button data-testid="wizard-save" disabled={saving || !!cele || (!balanced && !laterOk)} onClick={save} className="w-full h-14 text-lg bg-green-600 hover:bg-green-700 text-white rounded-2xl gap-2"><Check strokeWidth={2.5} /> {saving ? "Filing…" : "File it 🎉"}</Button>
        )}
      </div>

      {cele && <SlotCelebration win={cele.win} streak={cele.streak} onDone={() => navigate(cele.to)} />}

      {/* Crew multi-select */}
      <Dialog open={crewOpen} onOpenChange={setCrewOpen}>
        <DialogContent className="max-w-md h-[80vh] flex flex-col rounded-3xl p-0 gap-0">
          <DialogHeader className="p-4 pb-2"><DialogTitle className="font-heading text-slate-800">Tick the crew</DialogTitle><DialogDescription className="text-slate-400 text-sm">Search by worker #, name or ID and tick everyone who worked.</DialogDescription></DialogHeader>
          <div className="px-4 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input data-testid="crew-search" autoFocus placeholder="Type # then Enter, or search name…" value={crewQuery} onChange={(e) => setCrewQuery(e.target.value)} onKeyDown={onCrewKey} inputMode="text" className="pl-9 h-12 rounded-xl text-base" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 divide-y divide-slate-100">
            {filteredWorkers.map((w) => (
              <div key={w.id} data-testid={`crew-option-${w.id}`} role="button" tabIndex={0} onClick={() => toggleCrew(w)} className="w-full flex items-center gap-3 py-3 text-left cursor-pointer min-h-[48px]">
                <Checkbox checked={inCrew(w.id)} className="pointer-events-none data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600" />
                <span className="font-mono text-teal-600 w-12 text-sm font-semibold">#{w.number}</span>
                <span className="flex-1 text-slate-800">{w.name}</span>
                <span className="text-[11px] text-slate-400 font-mono">{w.code}</span>
              </div>
            ))}
            {filteredWorkers.length === 0 && <div className="text-center text-slate-400 py-8 text-sm">No worker found.</div>}
          </div>
          <div className="flex gap-2 p-4 border-t border-slate-100">
            <Button data-testid="crew-add-new" variant="outline" onClick={addNewWorker} className="flex-1 h-12 rounded-xl border-teal-300 text-teal-700 gap-1.5"><Plus size={16} strokeWidth={2.5} /> New worker</Button>
            <Button data-testid="crew-done" onClick={() => setCrewOpen(false)} className="flex-1 h-12 btn-grad text-white rounded-xl">Done ({entry.workers.length})</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ children, className = "" }) { return <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-4 ${className}`}>{children}</div>; }
function Label({ children }) { return <div className="text-sm font-semibold text-slate-700">{children}</div>; }
